import React from 'react';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { useFeatureGate } from '../../hooks/useSubscription';
import UpgradePrompt from './UpgradePrompt';

/**
 * Feature Gate Component - Controls access to features based on subscription tier
 *
 * @param {string} feature - The feature key to check access for (uses new entitlement keys)
 * @param {React.ReactNode} children - Content to show when feature is accessible
 * @param {React.ReactNode} fallback - Content to show when feature is locked (optional)
 * @param {boolean} showUpgradePrompt - Whether to show upgrade prompt when locked (default: true)
 * @param {boolean} blockInteraction - Whether to block interaction when disabled (default: true)
 * @param {string} customMessage - Custom message for locked features
 */
const FeatureGate = ({
  feature,
  children,
  fallback,
  showUpgradePrompt = true,
  blockInteraction = true,
  customMessage,
  className = '',
}) => {
  const gateProps = useFeatureGate(feature, {
    showUpgradePrompt,
    blockInteraction,
    customMessage,
  });

  const {
    isLocked,
    hasAccess,
    canUse,
    shouldShowUpgrade,
    upgradeInfo,
    lockMessage,
    onClick,
    className: gateClassName,
  } = gateProps;

  // If completely locked (no access to feature)
  if (isLocked) {
    if (fallback) {
      return fallback;
    }

    if (shouldShowUpgrade && upgradeInfo) {
      return (
        <UpgradePrompt
          feature={feature}
          recommendedTier={upgradeInfo.recommendedTier}
          message={lockMessage}
          className={className}
        />
      );
    }

    // Default locked state - premium glass overlay
    return (
      <div className={`relative ${className}`}>
        <div className="opacity-20 pointer-events-none blur-[1px]">{children}</div>
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-white/70 to-sage-50/80 backdrop-blur-xs rounded-xl border border-sage-200/60">
          <div className="text-center p-6">
            <div className="w-10 h-10 bg-lavender-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <LockClosedIcon className="w-5 h-5 text-lavender-600" />
            </div>
            <div className="text-sage-800 font-semibold mb-1 text-sm">Premium Feature</div>
            <div className="text-xs text-sage-500 max-w-[200px]">{lockMessage}</div>
          </div>
        </div>
      </div>
    );
  }

  // Has access but might be disabled due to usage limits
  if (hasAccess && !canUse) {
    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
      <div className={`${gateClassName} ${className}`} onClick={onClick}>
        <div className="opacity-50">{children}</div>
      </div>
    );
  }

  // Full access
  return <div className={className}>{children}</div>;
};

export default FeatureGate;
